import type {
  DocPackGeometryRecord,
  DocPackPage,
  DocPackPageCharRange,
  DocPackPageWord,
  DocPackRect,
} from '@listening/doc-pack';
import { decodeDocPackGeometry } from '@listening/doc-pack';
import type { DocPackTtsChunk } from '../query/tts-chunks';

export type DocPackOverlayWord = {
  readonly word: DocPackPageWord;
  readonly rects: readonly DocPackRect[];
  readonly active: boolean;
};

type PageGeometry = {
  readonly records: readonly DocPackGeometryRecord[];
  // `${charStart}:${charEnd}` → rects, so per-word lookups are O(1) instead of
  // a linear scan of every record on the page (docPackWordsForChunkOnPage runs
  // this per word on every playback tick).
  readonly rectsByCharRange: ReadonlyMap<string, readonly DocPackRect[]>;
};

const geometryRecordCache = new WeakMap<DocPackPage, PageGeometry>();

const charRangeKey = (chars: readonly [number, number]) =>
  `${chars[0].toString()}:${chars[1].toString()}`;

const rangeIntersects = (
  a: readonly [number, number],
  b: readonly [number, number],
) => a[0] < b[1] && b[0] < a[1];

const pageRangeIntersectsWord = (
  range: DocPackPageCharRange,
  word: DocPackPageWord,
) => rangeIntersects([range[1], range[2]], word.chars);

const rectContains = (rect: DocPackRect, x: number, y: number) =>
  x >= rect[0] && x <= rect[2] && y >= rect[1] && y <= rect[3];

const pageGeometry = (page: DocPackPage): PageGeometry => {
  const cached = geometryRecordCache.get(page);
  if (cached) return cached;

  const records = page.geometry
    ? decodeDocPackGeometry(page.geometry)
    : page.words.flatMap((word) =>
        word.rects.map((rect) => ({
          chars: word.chars,
          rect,
        })),
      );

  const rectsByCharRange = new Map<string, DocPackRect[]>();
  for (const record of records) {
    const key = charRangeKey(record.chars);
    const rects = rectsByCharRange.get(key);
    if (rects) {
      rects.push(record.rect);
    } else {
      rectsByCharRange.set(key, [record.rect]);
    }
  }

  const geometry: PageGeometry = { records, rectsByCharRange };
  geometryRecordCache.set(page, geometry);
  return geometry;
};

export function docPackGeometryRecordsForPage(
  page: DocPackPage,
): readonly DocPackGeometryRecord[] {
  return pageGeometry(page).records;
}

const wordForChars = (
  page: DocPackPage,
  chars: readonly [number, number],
): DocPackPageWord | undefined =>
  page.words.find(
    (word) => word.chars[0] === chars[0] && word.chars[1] === chars[1],
  ) ??
  page.words.find(
    (word) => word.chars[0] <= chars[0] && chars[1] <= word.chars[1],
  );

export function docPackRectsForWord(
  page: DocPackPage,
  word: DocPackPageWord,
): readonly DocPackRect[] {
  // No geometry record for the word → fall back to its own rects. A hidden
  // word (`rects: []`, no records) resolves to `[]` and draws no overlay boxes.
  return (
    pageGeometry(page).rectsByCharRange.get(charRangeKey(word.chars)) ??
    word.rects
  );
}

export function findDocPackWordAt(
  page: DocPackPage,
  x: number,
  y: number,
): DocPackPageWord | undefined {
  const record = docPackGeometryRecordsForPage(page).find((candidate) =>
    rectContains(candidate.rect, x, y),
  );
  return record ? wordForChars(page, record.chars) : undefined;
}

export function docPackWordsForChunkOnPage(
  page: DocPackPage,
  chunk:
    | Pick<DocPackTtsChunk, 'docPackChunk' | 'docPackWordTimings'>
    | undefined,
  positionMs: number,
): readonly DocPackOverlayWord[] {
  if (!chunk) return [];
  const ranges = chunk.docPackChunk.ranges.filter(
    (range) => range[0] === page.page,
  );
  if (ranges.length === 0) return [];
  const activeRanges = chunk.docPackWordTimings
    .filter(
      (timing) =>
        timing.page === page.page &&
        timing.start_ms <= positionMs &&
        positionMs <= timing.end_ms,
    )
    .map((timing) => [timing.char_start, timing.char_end] as const);

  return page.words
    .filter((word) =>
      ranges.some((range) => pageRangeIntersectsWord(range, word)),
    )
    .map((word) => ({
      word,
      rects: docPackRectsForWord(page, word),
      active: activeRanges.some((range) => rangeIntersects(range, word.chars)),
    }));
}

/** The box to place the reader at for a character offset. A word that shares
 * its box with the previous word is stored without one, so it resolves to the
 * nearest earlier boxed word, or the next one when nothing earlier is boxed. */
export function firstDocPackRectForAnchor(
  page: DocPackPage,
  charStart: number,
): DocPackRect | undefined {
  const exact = docPackGeometryRecordsForPage(page).find(
    (record) => record.chars[0] <= charStart && charStart < record.chars[1],
  )?.rect;
  if (exact) return exact;
  let before: { readonly start: number; readonly rect: DocPackRect } | null =
    null;
  let after: { readonly start: number; readonly rect: DocPackRect } | null =
    null;
  for (const word of page.words) {
    const rect = docPackRectsForWord(page, word)[0];
    if (!rect) continue;
    const start = word.chars[0];
    if (start <= charStart) {
      if (!before || start > before.start) before = { start, rect };
    } else if (!after || start < after.start) {
      after = { start, rect };
    }
  }
  return (before ?? after)?.rect;
}
